import React, { useState, useEffect } from "react";
import {
  Plus,
  Calendar,
  DollarSign,
  Users,
  TrendingUp,
  Eye,
  CheckCircle,
  FileText,
  Banknote,
  Clock,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import CreatePayrollRunModal from "@/components/CreatePayrollRunModal";
import PayrollRunDetailsModal from "@/components/PayrollRunDetailsModal";
import { formatCurrency } from "@/utils/currency";
import { formatDate } from "@/utils/date";

interface PayrollRun {
  id: number;
  periodStart: string;
  periodEnd: string;
  status: "draft" | "processing" | "completed" | "cancelled";
  totalEmployees: number;
  totalGrossPay: number;
  totalNetPay: number;
  totalDeductions: number;
  processedByUser: {
    firstName: string;
    lastName: string;
  };
  createdAt: string;
}

interface PayrollStats {
  totalEmployees: number;
  currentMonth: {
    runs: number;
    totalPayout: number;
  };
  yearToDate: {
    runs: number;
    totalPayout: number;
    totalTax: number;
    employeesProcessed: number;
  };
  recentRuns: PayrollRun[];
}

const Payroll: React.FC = () => {
  const { user } = useAuth();
  const [payrollRuns, setPayrollRuns] = useState<PayrollRun[]>([]);
  const [stats, setStats] = useState<PayrollStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPayrollRun, setSelectedPayrollRun] = useState<PayrollRun | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const canCreatePayroll = user?.role === "admin" || user?.role === "hr_manager";
  const canApprovePayroll = user?.role === "admin" || user?.role === "hr_manager";

  const fetchPayrollRuns = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(`/api/v1/payroll/runs?page=${page}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setPayrollRuns(result.data.payrollRuns);
        setTotalPages(result.data.pagination.totalPages);
      }
    } catch (error) {
      console.error("Error fetching payroll runs:", error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch("/api/v1/payroll/stats", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setStats(result.data);
      }
    } catch (error) {
      console.error("Error fetching payroll stats:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPayrollRuns(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [page]);

  const handleCreatePayroll = () => setShowCreateModal(true);
  const handlePayrollCreated = () => {
    setShowCreateModal(false);
    fetchPayrollRuns();
    fetchStats();
  };

  const handleViewDetails = (run: PayrollRun) => {
    setSelectedPayrollRun(run);
    setShowDetailsModal(true);
  };

  const handleApprovePayroll = async (id: number) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(`/api/v1/payroll/runs/${id}/approve`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        await fetchPayrollRuns();
        await fetchStats();
      } else {
        const error = await response.json();
        alert(error.message || "Failed to approve payroll run");
      }
    } catch {
      alert("Failed to approve payroll run");
    }
  };

  const getStatusBadge = (status: PayrollRun["status"]) => {
    const map = {
      completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
      cancelled: "bg-red-50 text-red-700 border-red-200",
      processing: "bg-amber-50 text-amber-700 border-amber-200",
      draft: "bg-indigo-50 text-indigo-700 border-indigo-200",
    };
    return map[status] || map.draft;
  };

  const totalPayout = stats?.yearToDate.totalPayout || 0;
  const totalTax = stats?.yearToDate.totalTax || 0;
  const totalEmployees = stats?.totalEmployees || 0;
  const currentMonthRuns = stats?.currentMonth.runs || 0;

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3 mb-4 lg:mb-0">
          <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center">
            <Banknote size={20} className="text-indigo-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Payroll Management</h1>
            <p className="text-gray-500 dark:text-gray-400 text-xs">
              Manage payroll runs, process payouts, and track employee compensation
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-white dark:bg-gray-800 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
            <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">YTD Payout</div>
            <div className="text-gray-900 dark:text-white font-bold text-sm">
              {formatCurrency(totalPayout, "USD")}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
            <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">This Month</div>
            <div className="text-gray-900 dark:text-white font-bold text-sm">{currentMonthRuns} runs</div>
          </div>
          {canCreatePayroll && (
            <button
              onClick={handleCreatePayroll}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition-all duration-200 hover:shadow-md text-xs"
            >
              <Plus size={12} />
              New Payroll Run
            </button>
          )}
        </div>
      </div>

      {/* Stats Section */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            {
              title: "Total Employees",
              value: totalEmployees,
              icon: Users,
              iconClass: "bg-indigo-50 text-indigo-600",
              subtitle: "Active workforce"
            },
            {
              title: "Current Month Runs",
              value: currentMonthRuns,
              icon: Calendar,
              iconClass: "bg-indigo-50 text-indigo-600",
              subtitle: "Payroll cycles"
            },
            {
              title: "YTD Payout",
              value: formatCurrency(totalPayout, "USD"),
              icon: DollarSign,
              iconClass: "bg-emerald-50 text-emerald-600",
              subtitle: "Total distributed"
            },
            {
              title: "YTD Taxes",
              value: formatCurrency(totalTax, "USD"),
              icon: TrendingUp,
              iconClass: "bg-amber-50 text-amber-600",
              subtitle: "Tax contributions"
            },
          ].map((stat, index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card"
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`h-8 w-8 rounded flex items-center justify-center ${stat.iconClass}`}>
                  <stat.icon size={14} />
                </div>
              </div>
              <p className="text-base font-bold text-gray-900 dark:text-white mb-1">{stat.value}</p>
              <p className="text-gray-600 dark:text-gray-300 font-medium text-xs mb-1">{stat.title}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{stat.subtitle}</p>
            </div>
          ))}
        </div>
      )}

      {/* Payroll Runs Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <FileText size={14} />
            Recent Payroll Runs
          </h2>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  {[
                    "Pay Period",
                    "Status",
                    "Employees",
                    "Gross Pay",
                    "Net Pay",
                    "Processed By",
                    "Actions",
                  ].map((col) => (
                    <th key={col} className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider text-xs">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {payrollRuns.map((run) => {
                  return (
                    <tr key={run.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-6 w-6 rounded flex items-center justify-center bg-indigo-50 text-indigo-600">
                            <Calendar size={12} />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white text-xs">
                              {formatDate(run.periodStart)} – {formatDate(run.periodEnd)}
                            </p>
                            <p className="text-gray-500 dark:text-gray-400 text-xs flex items-center gap-1">
                              <Clock size={10} />
                              {formatDate(run.createdAt)}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded text-xs font-medium border ${getStatusBadge(run.status)}`}>
                          {run.status.charAt(0).toUpperCase() + run.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-white text-xs">
                        {run.totalEmployees}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-white text-xs">
                        {formatCurrency(run.totalGrossPay, "USD")}
                      </td>
                      <td className="px-4 py-3 font-medium text-xs text-emerald-700">
                        {formatCurrency(run.totalNetPay, "USD")}
                      </td>
                      <td className="px-4 py-3 text-gray-900 dark:text-white font-medium text-xs">
                        {run.processedByUser.firstName} {run.processedByUser.lastName}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleViewDetails(run)}
                            className="text-gray-400 hover:text-indigo-600 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                            title="View Details"
                          >
                            <Eye size={12} />
                          </button>
                          {canApprovePayroll && run.status === "processing" && (
                            <button
                              onClick={() => handleApprovePayroll(run.id)}
                              className="text-gray-400 hover:text-emerald-700 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                              title="Approve Payroll"
                            >
                              <CheckCircle size={12} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty State */}
        {payrollRuns.length === 0 && !loading && (
          <div className="text-center py-8">
            <div className="h-12 w-12 mx-auto bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mb-3">
              <FileText className="text-gray-400 h-5 w-5" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
              No Payroll Runs Yet
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-xs mb-4">
              Get started by creating your first payroll run.
            </p>
            {canCreatePayroll && (
              <button
                onClick={handleCreatePayroll}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition-all duration-200 hover:shadow-md text-xs"
              >
                <Plus size={12} />
                Create Payroll Run
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center text-xs text-gray-600 dark:text-gray-300">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="px-3 py-1 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-all duration-200"
          >
            Previous
          </button>
          <span className="font-medium">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="px-3 py-1 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-all duration-200"
          >
            Next
          </button>
        </div>
      )}

      {/* Information Section */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
            <Clock size={14} className="text-indigo-600" />
            Payroll Schedule
          </h3>
          <p className="text-gray-600 dark:text-gray-300 text-xs mb-3">
            Payroll runs are typically processed monthly, with payments distributed
            by the end of each month for the current period.
          </p>
          <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
            <li className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-emerald-600 rounded-full"></div>
              Monthly processing cycle
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-emerald-600 rounded-full"></div>
              Payments by end of month
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-emerald-600 rounded-full"></div>
              Tax calculations automated
            </li>
          </ul>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
            <Users size={14} className="text-indigo-600" />
            Employee Coverage
          </h3>
          <p className="text-gray-600 dark:text-gray-300 text-xs mb-3">
            All active employees are included in payroll runs based on their
            employment status and compensation structure.
          </p>
          <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
            <li className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full"></div>
              Includes all active employees
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full"></div>
              Automatic tax calculations
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full"></div>
              Compliance with local regulations
            </li>
          </ul>
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreatePayrollRunModal
          onClose={() => setShowCreateModal(false)}
          onPayrollCreated={handlePayrollCreated}
        />
      )}
      {showDetailsModal && selectedPayrollRun && (
        <PayrollRunDetailsModal
          payrollRun={selectedPayrollRun}
          onClose={() => setShowDetailsModal(false)}
          onApproved={() => {
            fetchPayrollRuns();
            fetchStats();
          }}
        />
      )}
    </div>
  );
};

export default Payroll;
